import { createRouter } from '@tanstack/react-router'
import ErrorFallbackUI from 'components/ErrorFallbackUI/ErrorFallbackUI'
import FullpageLoader from 'components/Loader/FullpageLoader/FullpageLoader'
import { trackPageViews } from 'helpers/vendor/amplitude'
import { trackRumClickTrackingRoutes } from 'helpers/vendor/datadogClickActions'
import { routeTree } from 'routeTree.gen'

// These pages reset their own scroll position to the top on navigation (they need to scroll to
// top even when re-visiting a page whose cached scroll position isn't 0), so the router's own
// restore-or-reset handling is skipped here to avoid the two fighting over the final scroll offset.
const SELF_MANAGED_SCROLL_PATHNAMES = [
  /\/bot-detection\/events\/[^/]+$/,
  /\/workspaces\/[^/]+\/events\/[^/]+$/,
  /\/workspaces\/[^/]+\/webhooks\/[^/]+\/events$/,
]

// Create a new router instance
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultStaleTime: 5000,
  scrollRestoration: ({ location }) =>
    !SELF_MANAGED_SCROLL_PATHNAMES.some((pattern) => pattern.test(location.pathname)),
  defaultErrorComponent: ErrorFallbackUI,
  defaultPendingMs: 1000, // if the page components and route protection takes longer than this, show a full page loader
  defaultPendingMinMs: 300, // show the full page loader for at least this long to avoid flickering
  defaultPendingComponent: FullpageLoader,
  context: {
    queryClient: undefined!,
    auth: undefined!,
    buildRequest: undefined!,
  },
})

// Subscribe before the router renders, otherwise the initial page view is missed
trackPageViews(router)
trackRumClickTrackingRoutes(router)

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }

  interface StaticDataRouteOption {
    /** Sends this route's RUM click actions, and so its frustration signals; see `datadogClickActions`. */
    rumClickTracking?: boolean
  }

  interface HistoryState {
    isUserDialogOpen?: boolean
    didContactSupport?: boolean
    email?: string
    search?: string
  }
}
